import React, { useEffect, useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { useMutation, useQuery } from '@apollo/client';
import { ActivityIndicator } from 'react-native-paper';
import { css } from '@emotion/native';
import { ADMIN_SET_TEST_USER, GET_ADMIN_USERS } from '@/shared/queries/admin';
import {
  AdminUser,
  MutationAdminSetTestUserArgs,
  MutationResult,
  QueryAdminUsersArgs,
  QueryResult,
  UserType,
} from '@/shared/types/generated';
import StaticInput from '@/shared/components/StaticInput';
import Checkbox from '@/shared/components/Checkbox';
import SentryService from '@/services/sentry/Sentry.service';
import useStore from '@/stores/useStore';
import { green } from '@/shared/styles/colors';
import {
  Avatar,
  Cell,
  Column,
  ConfirmAction,
  EmailLink,
  EmptyState,
  ErrorState,
  Muted,
  Pill,
  SegmentedControl,
  Small,
  Spread,
  Stack,
  Strong,
  TableCard,
  TableHeader,
  TableRow,
} from './AdminUI';
import { ago, cityName, lastInApp, plural, shortDate } from './format';

type TypeFilter = 'all' | UserType;
type Period = '7' | '30' | '90' | 'all';

const COLUMNS: Column[] = [
  { key: 'person', label: 'Person', flex: 3 },
  { key: 'type', label: 'Type', flex: 1.8 },
  { key: 'joined', label: 'Joined', flex: 1.4 },
  { key: 'sessions', label: 'Sessions', flex: 1.2 },
  { key: 'seen', label: 'Last in app', flex: 1.4 },
  { key: 'test', label: 'Test account', flex: 1.8 },
];

const firstName = (user: AdminUser) => user.name.split(' ')[0] || user.name;

/** Spells out what the flag does, since it hides stylists from clients */
const testQuestion = (user: AdminUser) => {
  if (user.isTestUser) {
    return user.type === UserType.Stylist
      ? `Show ${firstName(user)} to clients again and count them in the numbers?`
      : `Count ${firstName(user)} in the numbers again?`;
  }
  return user.type === UserType.Stylist
    ? `Hide ${firstName(user)} from clients and leave them out of the numbers?`
    : `Leave ${firstName(user)} out of the numbers?`;
};

const UsersPanel = ({ isDesktop }: { isDesktop: boolean }) => {
  const { setSnackbarState } = useStore();
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [period, setPeriod] = useState<Period>('30');
  const [includeTestUsers, setIncludeTestUsers] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Wait for a pause in typing before searching
  useEffect(() => {
    const timeout = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const { data, loading, error, refetch } = useQuery<
    QueryResult<'adminUsers'>,
    QueryAdminUsersArgs
  >(GET_ADMIN_USERS, {
    variables: {
      search: search || null,
      type: typeFilter === 'all' ? null : typeFilter,
      days: period === 'all' ? null : Number(period),
      allTime: period === 'all',
      includeTestUsers,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const [setTestUser] = useMutation<
    MutationResult<'adminSetTestUser'>,
    MutationAdminSetTestUserArgs
  >(ADMIN_SET_TEST_USER);

  const users = data?.adminUsers.users ?? [];
  // Counts come from the server so they cover every match, not just this page
  const clients = data?.adminUsers.clientCount ?? 0;
  const stylists = data?.adminUsers.stylistCount ?? 0;
  const totalCount = data?.adminUsers.totalCount ?? 0;
  const windowText = period === 'all' ? 'in all' : `in the last ${period} days`;

  const summary = () => {
    if (search) {
      return `${plural(totalCount, 'match', 'matches')} for "${search}" across everyone`;
    }
    const people =
      typeFilter === 'all'
        ? `${plural(clients + stylists, period === 'all' ? 'person' : 'sign-up', period === 'all' ? 'people' : 'sign-ups')} ${windowText} · ${plural(clients, 'client')}, ${plural(stylists, 'stylist')}`
        : `${plural(totalCount, typeFilter === UserType.Stylist ? 'stylist' : 'client')} ${windowText}`;
    return `${people}${includeTestUsers ? ' · test accounts shown but not counted' : ''}`;
  };

  const onToggleTestUser = async (user: AdminUser) => {
    try {
      await setTestUser({
        variables: { userId: user.userId, isTestUser: !user.isTestUser },
      });
      await refetch();
    } catch (toggleError) {
      SentryService.captureError(
        'Admin failed to update test user flag',
        toggleError,
      );
      setSnackbarState({
        open: true,
        message: 'Couldn’t update that user',
        severity: 'error',
      });
    }
  };

  const renderEmpty = () => {
    if (search) {
      return (
        <EmptyState
          title="No matches"
          body={`Nobody's name or email matches "${search}"${includeTestUsers ? '' : ', not counting test accounts'}.`}
        />
      );
    }
    return (
      <EmptyState
        title={period === 'all' ? 'Nobody yet' : 'No sign-ups'}
        body={
          period === 'all'
            ? 'Nobody matches these filters.'
            : `Nobody signed up ${windowText}. Try a longer period or "Any time".`
        }
      />
    );
  };

  return (
    <View>
      <Spread style={{ alignItems: 'flex-end' }}>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 24 }}>
          <SegmentedControl
            label="Show"
            selected={typeFilter}
            onSelect={setTypeFilter}
            options={[
              { value: 'all', label: 'Everyone' },
              { value: UserType.Client, label: 'Clients' },
              { value: UserType.Stylist, label: 'Stylists' },
            ]}
          />
          {/* A search looks across everyone, whenever they joined */}
          <SegmentedControl
            label={search ? 'Joined (not used while searching)' : 'Joined'}
            disabled={Boolean(search)}
            selected={period}
            onSelect={setPeriod}
            options={[
              { value: '7', label: '7 days' },
              { value: '30', label: '30 days' },
              { value: '90', label: '90 days' },
              { value: 'all', label: 'Any time' },
            ]}
          />
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
          <TouchableOpacity
            onPress={() => setIncludeTestUsers((v) => !v)}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: includeTestUsers }}
            style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
          >
            <Checkbox
              checked={includeTestUsers}
              onPress={() => setIncludeTestUsers((v) => !v)}
            />
            <Muted>Show test accounts</Muted>
          </TouchableOpacity>
          <View style={{ width: isDesktop ? 260 : 180 }}>
            <StaticInput
              labelText="Search everyone"
              showLabel
              placeholderText="Name or email"
              value={searchInput}
              onChange={(event) => setSearchInput(event.nativeEvent.text)}
              customStyles={css({
                height: 42,
                fontSize: 13,
                color: green,
                width: '100%',
              })}
            />
          </View>
        </View>
      </Spread>

      {data ? (
        <Muted style={{ marginBottom: 14 }}>
          {summary()}
          {totalCount > users.length
            ? ` · showing the newest ${users.length} of ${totalCount}`
            : ''}
        </Muted>
      ) : null}

      {loading && !data ? (
        <ActivityIndicator
          size="large"
          color={green}
          style={{ marginTop: 80 }}
        />
      ) : error && !data ? (
        <ErrorState what="users" onRetry={() => refetch()} />
      ) : users.length === 0 ? (
        renderEmpty()
      ) : (
        <TableCard>
          {isDesktop ? <TableHeader columns={COLUMNS} /> : null}
          {users.map((user, index) => {
            const city = cityName(user.city);
            return (
              <TableRow
                key={user.userId}
                isDesktop={isDesktop}
                isFirst={index === 0}
              >
                <Cell flex={3} isDesktop={isDesktop}>
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 12,
                    }}
                  >
                    <Avatar
                      uri={user.profilePicUrl}
                      name={user.name}
                      size={36}
                    />
                    <Stack style={{ flexShrink: 1 }}>
                      <Strong numberOfLines={1}>{user.name}</Strong>
                      <EmailLink email={user.email} />
                      {city ? <Small>{city}</Small> : null}
                    </Stack>
                  </View>
                </Cell>
                <Cell flex={1.8} isDesktop={isDesktop}>
                  <View
                    style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}
                  >
                    <Pill
                      text={
                        user.type === UserType.Stylist ? 'Stylist' : 'Client'
                      }
                      tone="neutral"
                    />
                    {user.isTestUser ? (
                      <Pill text="Test" tone="neutral" />
                    ) : null}
                  </View>
                  {!user.setupComplete ? (
                    <Small style={{ marginTop: 4 }}>Stripe not set up</Small>
                  ) : null}
                </Cell>
                <Cell flex={1.4} isDesktop={isDesktop}>
                  <Stack>
                    <Small style={{ color: green }}>
                      {shortDate(user.createdAt)}
                    </Small>
                    <Small>{ago(user.createdAt)}</Small>
                  </Stack>
                </Cell>
                <Cell flex={1.2} isDesktop={isDesktop}>
                  <Small>{user.completedSessions} completed</Small>
                </Cell>
                <Cell flex={1.4} isDesktop={isDesktop}>
                  <Small>{lastInApp(user.lastSeenAt)}</Small>
                </Cell>
                <Cell flex={1.8} isDesktop={isDesktop}>
                  <ConfirmAction
                    label={user.isTestUser ? 'Unmark test' : 'Mark as test'}
                    question={testQuestion(user)}
                    confirmText={user.isTestUser ? 'Yes, unmark' : 'Yes, mark'}
                    onConfirm={() => onToggleTestUser(user)}
                  />
                </Cell>
              </TableRow>
            );
          })}
        </TableCard>
      )}
    </View>
  );
};

export default UsersPanel;
